import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { MdmShell, Field, StatusBadge, ErrorAlert, s } from '../mdm/_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  listProfileVendors, getVendorProfile, lookupPincode,
  saveVendorGeneral, saveVendorBank, saveVendorGst,
  type VendorListItem, type VendorProfile, type PincodeMatch,
} from '../../api/vendorProfileApi.js';
import { ReleasedOrdersTable } from './ReleasedOrdersTable.js';
import {
  listPlanningCycles, listBrandingTaxonomy, getVendorBrandingProfile,
  saveVendorBrandingProfile,
  type PlanningCycle, type TaxonomyRow, type VendorBrandingProfile,
} from '../../api/vendorBrandingProfileApi.js';
import {
  listGeoScopes, assignGeoScope, revokeGeoScope, type GeoScopeRow,
} from '../../api/orgApi.js';
import { VendorGeographyPicker, EMPTY_CHAIN, type GeoChain } from './VendorGeographyPicker.js';

type Tab = 'GENERAL' | 'BANK_GST' | 'BRANDING_GEO' | 'RELEASED_ORDERS';

interface GeneralForm {
  vendorName: string; legalName: string; contactPerson: string;
  mobileNumber: string; email: string; address: string; geo: GeoChain;
}
interface BankGstForm {
  accountNumber: string; ifscCode: string; accountHolderName: string;
  bankName: string; branchName: string; accountTypeCode: string;
  gstNumber: string; panNumber: string;
}

const EMPTY_GENERAL: GeneralForm = {
  vendorName: '', legalName: '', contactPerson: '', mobileNumber: '',
  email: '', address: '', geo: EMPTY_CHAIN,
};
const EMPTY_BANKGST: BankGstForm = {
  accountNumber: '', ifscCode: '', accountHolderName: '', bankName: '',
  branchName: '', accountTypeCode: 'CURRENT', gstNumber: '', panNumber: '',
};

const row = { display: 'flex', gap: 12, flexWrap: 'wrap' as const };

/**
 * Vendor Profile — everything about a vendor on one screen.
 *
 * Nothing here is a new store. Identity, address, bank and GST come from the
 * vendor master; branding types per year from the existing M17 branding profile
 * API; where the vendor works from the existing geography scope API.
 *
 * View first: fields become editable only after Modify. Save compares against
 * the snapshot and writes nothing when nothing changed; Cancel restores it.
 * Login id and vendor code are never editable — they identify the vendor.
 */
export function VendorProfilePage(): ReactNode {
  const { t: tr } = useTranslation();
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState<VendorListItem[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [profile, setProfile] = useState<VendorProfile | null>(null);
  const [tab, setTab] = useState<Tab>('GENERAL');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [general, setGeneral] = useState<GeneralForm>(EMPTY_GENERAL);
  const [bankGst, setBankGst] = useState<BankGstForm>(EMPTY_BANKGST);
  const [pinInput, setPinInput] = useState('');
  const [pinNote, setPinNote] = useState<string | null>(null);
  /** Cities carrying the typed pincode. While set, the City list offers only
   *  these, so the list agrees with the pincode above it. */
  const [pinCities, setPinCities] = useState<readonly PincodeMatch[] | null>(null);

  /** A pincode can straddle two sub-districts and the village directory repeats
   *  names across them, so the sub-district is shown when that happens. */
  const cityOptions = useMemo(() => {
    if (!pinCities) return undefined;
    const manyTehsils = new Set(pinCities.map((h) => h.tehsilId)).size > 1;
    return pinCities
      .map((h) => ({
        id: h.cityId,
        name: manyTehsils ? `${h.cityName} (${h.tehsilName})` : h.cityName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [pinCities]);

  /**
   * Fill the whole address chain from a pincode. A pincode can cover several
   * villages, so the levels they all agree on are set and the city is left for
   * the operator when they differ.
   */
  const applyPincode = async (pin: string) => {
    setPinNote(null);
    setPinCities(null);
    if (!/^[1-9][0-9]{5}$/.test(pin)) { setPinNote(tr('vendorProfile.pinInvalid')); return; }
    try {
      const hits = await lookupPincode(pin, token);
      if (hits.length === 0) { setPinNote(tr('vendorProfile.pinNotFound')); return; }
      const same = <K extends keyof typeof hits[0]>(k: K) =>
        hits.every((h) => h[k] === hits[0][k]) ? (hits[0][k] as string) : '';
      setGeneral((g) => ({
        ...g,
        geo: {
          stateId: same('stateId'),
          districtId: same('districtId'),
          tehsilId: same('tehsilId'),
          cityId: hits.length === 1 ? hits[0].cityId : '',
        },
      }));
      setPinCities(hits);
      setPinNote(hits.length === 1
        ? `${hits[0].cityName}, ${hits[0].tehsilName}, ${hits[0].districtName}, ${hits[0].stateName}`
        : tr('vendorProfile.pinManyCities', { count: hits.length }));
    } catch (e) {
      setPinNote((e as Error).message);
    }
  };

  useEffect(() => {
    listProfileVendors(token).then(setVendors).catch((e) => setError((e as Error).message));
  }, [token]);

  const hydrate = useCallback((p: VendorProfile) => {
    setGeneral({
      vendorName: p.general.vendorName ?? '', legalName: p.general.legalName ?? '',
      contactPerson: p.general.contactPerson ?? '', mobileNumber: p.general.mobileNumber ?? '',
      email: p.general.email ?? '', address: p.general.address ?? '',
      geo: {
        stateId: p.general.addressStateId ?? '', districtId: p.general.addressDistrictId ?? '',
        tehsilId: p.general.addressTehsilId ?? '', cityId: p.general.addressCityId ?? '',
      },
    });
    setBankGst({
      accountNumber: p.bank.accountNumber ?? '', ifscCode: p.bank.ifscCode ?? '',
      accountHolderName: p.bank.accountHolderName ?? '', bankName: p.bank.bankName ?? '',
      branchName: p.bank.branchName ?? '', accountTypeCode: p.bank.accountTypeCode ?? 'CURRENT',
      gstNumber: p.gst.gstNumber ?? '', panNumber: p.gst.panNumber ?? '',
    });
    // The pincode box shows what the saved city carries until it is retyped.
    setPinInput(p.general.addressPincode ?? '');
    setPinNote(null);
    setPinCities(null);
  }, []);

  const load = useCallback((id: string) => {
    if (!id) { setProfile(null); return; }
    getVendorProfile(id, token)
      .then((p) => { setProfile(p); hydrate(p); })
      .catch((e) => setError((e as Error).message));
  }, [token, hydrate]);

  useEffect(() => { load(vendorId); }, [vendorId, load]);

  const onCancel = () => {
    if (profile) hydrate(profile);
    setEditing(false); setNotice(null); setError(null);
  };

  const onSave = async () => {
    if (!profile) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      let changed = false;
      if (tab === 'GENERAL') {
        const r = await saveVendorGeneral(profile.general.vendorId, {
          vendorName: general.vendorName, legalName: general.legalName,
          contactPerson: general.contactPerson, mobileNumber: general.mobileNumber,
          email: general.email || undefined, address: general.address,
          addressStateId: general.geo.stateId || undefined,
          addressDistrictId: general.geo.districtId || undefined,
          addressTehsilId: general.geo.tehsilId || undefined,
          addressCityId: general.geo.cityId || undefined,
          pincode: pinInput || undefined,
        }, token);
        changed = r.changed;
      } else {
        // Bank and GST share a tab but are separate writes on the vendor master.
        const bank = await saveVendorBank(profile.general.vendorId, {
          accountNumber: bankGst.accountNumber, ifscCode: bankGst.ifscCode,
          accountHolderName: bankGst.accountHolderName || undefined,
          bankName: bankGst.bankName || undefined,
          branchName: bankGst.branchName || undefined,
          accountTypeCode: bankGst.accountTypeCode || undefined,
        }, token);
        const gst = await saveVendorGst(profile.general.vendorId, {
          gstNumber: bankGst.gstNumber || undefined,
          panNumber: bankGst.panNumber || undefined,
        }, token);
        changed = bank.changed || gst.changed;
      }
      setNotice(changed ? tr('vendorProfile.saved') : tr('vendorProfile.noChanges'));
      setEditing(false);
      if (changed) load(profile.general.vendorId);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  const ro = !editing;
  const tabBtn = (key: Tab, label: string, testId: string) => (
    <button
      type="button" role="tab" aria-selected={tab === key} aria-label={testId}
      disabled={editing} onClick={() => setTab(key)}
      style={{
        ...s.ghostBtn,
        borderBottom: tab === key ? '2px solid var(--color-brand-primary)' : s.ghostBtn.border,
        fontWeight: tab === key ? 600 : 400,
      }}
    >{label}</button>
  );

  return (
    <MdmShell
      label="vendor-profile"
      title={tr('vendorProfile.title')}
      description={tr('vendorProfile.desc')}
      actions={
        <Button type="button" variant="primary" data-testid="new-vendor"
          onClick={() => navigate('/admin/vendors/new')}>
          {tr('vendorProfile.newVendor')}
        </Button>
      }
    >
      <div style={{ ...row, alignItems: 'flex-end', marginBottom: 16 }}>
        <Field id="vp-vendor" label={tr('vendorProfile.selectVendor')}>
          <select id="vp-vendor" aria-label="vendor-selector" style={s.input}
            value={vendorId} disabled={editing}
            onChange={(e) => { setVendorId(e.target.value); setEditing(false); setNotice(null); }}>
            <option value="">{tr('vendorProfile.selectVendor')}</option>
            {vendors.map((v) => (
              <option key={v.vendorId} value={v.vendorId}>{v.vendorName} ({v.vendorCode})</option>
            ))}
          </select>
        </Field>
        {profile ? <StatusBadge status={profile.general.status} /> : null}
      </div>

      <ErrorAlert message={error} />
      {notice ? <p role="status" style={{ color: s.muted }}>{notice}</p> : null}

      {!profile ? (
        <p style={{ color: s.muted }}>{tr('vendorProfile.choosePrompt')}</p>
      ) : (
        <>
          <div role="tablist" aria-label="vendor-profile-tabs"
            style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {tabBtn('GENERAL', tr('vendorProfile.tabGeneral'), 'tab-general')}
            {tabBtn('BANK_GST', tr('vendorProfile.tabBankGst'), 'tab-bank-gst')}
            {tabBtn('BRANDING_GEO', tr('vendorProfile.tabBrandingGeo'), 'tab-branding-geo')}
            {tabBtn('RELEASED_ORDERS', tr('vendorProfile.tabReleasedOrders'), 'tab-released-orders')}
          </div>

          {tab === 'GENERAL' && (
            <section aria-label="general-information" style={s.card}>
              <p style={{ marginTop: 0, fontSize: 13, color: s.muted }} aria-label="active-since">
                {tr('vendorProfile.activeSince')}: <strong>{day(profile.general.activeSince)}</strong>
                {'  ·  '}{tr('vendorProfile.validFrom')}: <strong>{day(profile.general.lastModifiedOn)}</strong>
              </p>
              <div style={row}>
                <Field id="vp-login" label={tr('vendorProfile.loginId')}>
                  <input id="vp-login" aria-label="login-id" readOnly disabled style={s.input}
                    value={profile.general.loginId ?? '\u2014'} />
                </Field>
                <Field id="vp-code" label={tr('vendorProfile.vendorCode')}>
                  <input id="vp-code" aria-label="vendor-code" readOnly disabled style={s.input}
                    value={profile.general.vendorCode} />
                </Field>
                <Field id="vp-name" label={tr('vendorProfile.vendorName')}>
                  <input id="vp-name" aria-label="vendor-name" readOnly={ro} disabled={ro} style={s.input}
                    value={general.vendorName}
                    onChange={(e) => setGeneral({ ...general, vendorName: e.target.value })} />
                </Field>
                <Field id="vp-legal" label={tr('vendorProfile.legalName')}>
                  <input id="vp-legal" aria-label="legal-name" readOnly={ro} disabled={ro} style={s.input}
                    value={general.legalName}
                    onChange={(e) => setGeneral({ ...general, legalName: e.target.value })} />
                </Field>
                <Field id="vp-contact" label={tr('vendorProfile.contactPerson')}>
                  <input id="vp-contact" aria-label="contact-person" readOnly={ro} disabled={ro} style={s.input}
                    value={general.contactPerson}
                    onChange={(e) => setGeneral({ ...general, contactPerson: e.target.value })} />
                </Field>
                <Field id="vp-mobile" label={tr('vendorProfile.mobileNumber')} hint={tr('vendorProfile.mobileHint')}>
                  <input id="vp-mobile" aria-label="mobile-number" readOnly={ro} disabled={ro} style={s.input}
                    value={general.mobileNumber}
                    onChange={(e) => setGeneral({ ...general, mobileNumber: e.target.value })} />
                </Field>
                <Field id="vp-email" label={tr('vendorProfile.email')}>
                  <input id="vp-email" aria-label="email" readOnly={ro} disabled={ro} style={s.input}
                    value={general.email}
                    onChange={(e) => setGeneral({ ...general, email: e.target.value })} />
                </Field>
              </div>

              <h3 style={{ fontSize: 14, marginBottom: 8 }}>{tr('vendorProfile.address')}</h3>
              <div style={row}>
                {/* The only typed part of an address: the street line. Every
                    place below it is chosen from a list. */}
                <Field id="vp-address" label={tr('vendorProfile.streetLine')} hint={tr('vendorProfile.streetHint')}>
                  <input id="vp-address" aria-label="address" readOnly={ro} disabled={ro} style={s.input}
                    value={general.address}
                    onChange={(e) => setGeneral({ ...general, address: e.target.value })} />
                </Field>

                {/* One pincode box, not two: type it to fill the four lists
                    below, and after a save it shows the value the chosen city
                    carries. */}
                <Field id="vp-pin" label={tr('vendorProfile.pincode')} hint={tr('vendorProfile.pinLookupHint')}>
                  <input id="vp-pin" aria-label="pincode" readOnly={ro} disabled={ro} style={s.input}
                    inputMode="numeric" maxLength={6} value={pinInput}
                    onChange={(e) => {
                      setPinInput(e.target.value.replace(/\D/g, ''));
                      // Retyping the pincode makes the old shortlist stale.
                      setPinCities(null);
                      setPinNote(null);
                    }}
                    onBlur={() => { if (pinInput && pinInput !== (profile.general.addressPincode ?? '')) void applyPincode(pinInput); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void applyPincode(pinInput); }
                    }} />
                </Field>
              </div>
              {pinNote ? (
                <p role="status" aria-label="pincode-note" style={{ fontSize: 13, color: s.muted, marginTop: 0 }}>
                  {pinNote}
                </p>
              ) : null}
              <div style={row}>
                <VendorGeographyPicker
                  value={general.geo} disabled={ro}
                  cityOptions={cityOptions}
                  onChange={(geo) => {
                    const hit = pinCities?.find((h) => h.cityId === geo.cityId);
                    if (hit) {
                      // Picking from the shortlist settles the levels above it too.
                      setGeneral({
                        ...general,
                        geo: {
                          stateId: hit.stateId, districtId: hit.districtId,
                          tehsilId: hit.tehsilId, cityId: hit.cityId,
                        },
                      });
                      setPinNote(`${hit.cityName}, ${hit.tehsilName}, ${hit.districtName}, ${hit.stateName}`);
                      return;
                    }
                    // Moving away from the pincode's own area drops its shortlist.
                    if (pinCities && geo.tehsilId !== general.geo.tehsilId) {
                      setPinCities(null);
                      setPinNote(null);
                    }
                    setGeneral({ ...general, geo });
                  }}
                />
              </div>
              <p style={{ fontSize: 12, color: s.muted, marginBottom: 0 }}>
                {tr('vendorProfile.cityOptional')}
              </p>
            </section>
          )}

          {tab === 'BANK_GST' && (
            <section aria-label="bank-and-gst" style={s.card}>
              <h3 style={{ fontSize: 14, marginTop: 0 }}>{tr('vendorProfile.bankDetails')}</h3>
              <div style={row}>
                <Field id="vp-holder" label={tr('vendorProfile.accountHolder')}>
                  <input id="vp-holder" aria-label="account-holder" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.accountHolderName}
                    onChange={(e) => setBankGst({ ...bankGst, accountHolderName: e.target.value })} />
                </Field>
                <Field id="vp-bank" label={tr('vendorProfile.bankName')}>
                  <input id="vp-bank" aria-label="bank-name" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.bankName}
                    onChange={(e) => setBankGst({ ...bankGst, bankName: e.target.value })} />
                </Field>
                <Field id="vp-branch" label={tr('vendorProfile.branchName')}>
                  <input id="vp-branch" aria-label="branch-name" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.branchName}
                    onChange={(e) => setBankGst({ ...bankGst, branchName: e.target.value })} />
                </Field>
                <Field id="vp-acct" label={tr('vendorProfile.accountNumber')}>
                  <input id="vp-acct" aria-label="account-number" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.accountNumber}
                    onChange={(e) => setBankGst({ ...bankGst, accountNumber: e.target.value })} />
                </Field>
                <Field id="vp-ifsc" label={tr('vendorProfile.ifsc')} hint={tr('vendorProfile.ifscHint')}>
                  <input id="vp-ifsc" aria-label="ifsc-code" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.ifscCode}
                    onChange={(e) => setBankGst({ ...bankGst, ifscCode: e.target.value.toUpperCase() })} />
                </Field>
                <Field id="vp-acct-type" label={tr('vendorProfile.accountType')}>
                  <select id="vp-acct-type" aria-label="account-type" disabled={ro} style={s.input}
                    value={bankGst.accountTypeCode}
                    onChange={(e) => setBankGst({ ...bankGst, accountTypeCode: e.target.value })}>
                    <option value="CURRENT">{tr('vendorProfile.acctCurrent')}</option>
                    <option value="SAVINGS">{tr('vendorProfile.acctSavings')}</option>
                    <option value="CASH_CREDIT">{tr('vendorProfile.acctCashCredit')}</option>
                    <option value="OVERDRAFT">{tr('vendorProfile.acctOverdraft')}</option>
                  </select>
                </Field>
              </div>

              <h3 style={{ fontSize: 14 }}>{tr('vendorProfile.gstDetails')}</h3>
              <div style={row}>
                <Field id="vp-gstin" label={tr('vendorProfile.colGstin')} hint={tr('vendorProfile.gstinHint')}>
                  <input id="vp-gstin" aria-label="gstin" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.gstNumber}
                    onChange={(e) => setBankGst({ ...bankGst, gstNumber: e.target.value.toUpperCase() })} />
                </Field>
                <Field id="vp-pan" label={tr('vendorProfile.pan')}>
                  <input id="vp-pan" aria-label="pan-number" readOnly={ro} disabled={ro} style={s.input}
                    value={bankGst.panNumber}
                    onChange={(e) => setBankGst({ ...bankGst, panNumber: e.target.value.toUpperCase() })} />
                </Field>
              </div>
            </section>
          )}

          {tab === 'BRANDING_GEO' && (
            <BrandingGeoTab vendorId={profile.general.vendorId}
              onNotice={setNotice} onError={setError} />
          )}

          {tab === 'RELEASED_ORDERS' && (
            <ReleasedOrdersTable
              vendorId={profile.general.vendorId}
              titleKey="vendorProfile.releasedOrders"
              emptyKey="vendorProfile.noReleasedOrders"
              onError={setError}
            />
          )}

          {/* Branding/geography and the read-only released-orders view have no
              record edit here, so the modify toggle belongs to the master tabs. */}
          {tab !== 'BRANDING_GEO' && tab !== 'RELEASED_ORDERS' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {!editing ? (
                <Button type="button" variant="primary" data-testid="modify"
                  onClick={() => { setEditing(true); setNotice(null); }}>
                  {tr('vendorProfile.modify')}
                </Button>
              ) : (
                <>
                  <Button type="button" variant="primary" data-testid="save" disabled={busy}
                    onClick={() => void onSave()}>
                    {busy ? tr('vendorProfile.saving') : tr('vendorProfile.save')}
                  </Button>
                  <Button type="button" variant="secondary" data-testid="cancel" disabled={busy}
                    onClick={onCancel}>
                    {tr('vendorProfile.cancel')}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </>
      )}
    </MdmShell>
  );
}

interface BrandingGeoProps {
  readonly vendorId: string;
  readonly onNotice: (m: string) => void;
  readonly onError: (m: string) => void;
}

/**
 * Branding types per financial year, and the places the vendor works.
 *
 * Both halves are served by the endpoints that already own them, so this is a
 * view onto existing data rather than a second copy. The geography recorded
 * here is what /v1/geo-scope/eligible-vendors reads when a work order is raised
 * for a district.
 */
function BrandingGeoTab({ vendorId, onNotice, onError }: BrandingGeoProps): ReactNode {
  const { t: tr } = useTranslation();
  const { day } = useBusinessDay();
  const { token } = useAuth();

  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [taxonomy, setTaxonomy] = useState<readonly TaxonomyRow[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [profile, setProfile] = useState<VendorBrandingProfile | null>(null);
  const [chargeBasis, setChargeBasis] = useState('');
  const [scopes, setScopes] = useState<readonly GeoScopeRow[]>([]);
  const [geo, setGeo] = useState<GeoChain>(EMPTY_CHAIN);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listPlanningCycles(token).then((c) => {
      setCycles(c);
      if (c.length) setCycleId((cur) => cur || c[0].planningCycleId);
    }).catch((e) => onError((e as Error).message));
    listBrandingTaxonomy(token).then(setTaxonomy).catch((e) => onError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadScopes = useCallback(() => {
    listGeoScopes(token, 'VENDOR', vendorId).then(setScopes)
      .catch((e) => onError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vendorId]);

  useEffect(() => { loadScopes(); }, [loadScopes]);

  useEffect(() => {
    if (!cycleId) return;
    getVendorBrandingProfile(vendorId, cycleId, token)
      .then((p) => {
        setProfile(p);
        setChargeBasis(p.taxChargeBasisCode ?? '');
        setChosen(new Set(p.scope.map((x) => `${x.brandingTypeId}|${x.brandingItemId ?? ''}`)));
      })
      .catch((e) => onError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, cycleId, token]);

  const toggle = (key: string) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key); else next.add(key);
    setChosen(next);
  };

  const saveBranding = async () => {
    setBusy(true);
    try {
      await saveVendorBrandingProfile({
        vendorId, planningCycleId: cycleId, remarks: profile?.remarks ?? undefined,
        taxChargeBasisCode: chargeBasis,
        scope: [...chosen].map((k) => {
          const [brandingTypeId, itemId] = k.split('|');
          return itemId ? { brandingTypeId, brandingItemId: itemId } : { brandingTypeId };
        }),
      }, token);
      onNotice(tr('vendorProfile.saved'));
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  const addPlace = async () => {
    // The deepest level chosen decides the scope type, which is the shape
    // t626_geography_scope accepts.
    const scopeType = geo.tehsilId ? 'SUBDISTRICT' : geo.districtId ? 'DISTRICT' : geo.stateId ? 'STATE' : '';
    if (!scopeType) { onError(tr('vendorProfile.geoPickFirst')); return; }
    setBusy(true);
    try {
      await assignGeoScope(token, {
        ownerType: 'VENDOR', ownerId: vendorId,
        scopeType: scopeType as 'STATE' | 'DISTRICT' | 'SUBDISTRICT',
        stateId: geo.stateId || null,
        districtId: geo.districtId || null,
        tehsilId: geo.tehsilId || null,
        effectiveFromDate: new Date().toISOString().slice(0, 10),
      });
      setGeo(EMPTY_CHAIN); loadScopes(); onNotice(tr('vendorProfile.saved'));
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  const removePlace = async (scopeId: string) => {
    setBusy(true);
    try { await revokeGeoScope(token, scopeId); loadScopes(); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  const types = [...new Map(taxonomy.map((x) => [x.brandingTypeId, x])).values()];

  return (
    <>
      <section aria-label="branding-and-geography" style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h3 style={{ fontSize: 14, marginTop: 0 }}>{tr('vendorProfile.brandingScope')}</h3>
          {/* The boxes below only offer what the master already holds, so the
              way to add a missing one has to be reachable from here. */}
          <Link to="/admin/config-extended" aria-label="manage-branding-types"
            data-testid="manage-branding-types"
            style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {tr('vendorProfile.manageBrandingTypes')}
          </Link>
        </div>
        <div style={{ ...row, alignItems: 'flex-end', marginBottom: 12 }}>
          <Field id="vp-bg-cycle" label={tr('vendorProfile.colFy')}>
            <select id="vp-bg-cycle" aria-label="branding-cycle" style={s.input}
              value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
              {cycles.map((c) => (
                <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleCode}</option>
              ))}
            </select>
          </Field>
          <Field id="vp-bg-charge-basis" label={tr('vendorProfile.chargeBasis')}>
            <select id="vp-bg-charge-basis" aria-label="charge-basis" style={s.input}
              value={chargeBasis} onChange={(e) => setChargeBasis(e.target.value)}>
              <option value="">{tr('vendorProfile.chargeBasisPick')}</option>
              {(profile?.chargeBasisOptions ?? []).map((o) => (
                <option key={o.optionCode} value={o.optionCode}>{o.optionLabel}</option>
              ))}
            </select>
          </Field>
        </div>
        {cycleId && profile && !chargeBasis ? (
          <p role="alert" data-testid="charge-basis-missing" style={{ fontSize: 13 }}>
            {tr('vendorProfile.chargeBasisMissing')}
          </p>
        ) : null}

        <div style={row}>
          {types.map((t) => (
            <fieldset key={t.brandingTypeId} aria-label={`branding-type-${t.typeCode}`}
              style={{ border: `1px solid var(--color-border-default)`, borderRadius: 6, padding: 10, minWidth: 220 }}>
              <legend style={{ fontSize: 12, color: s.muted }}>{t.typeName}</legend>
              {taxonomy.filter((x) => x.brandingTypeId === t.brandingTypeId).map((x) => {
                const key = `${x.brandingTypeId}|${x.brandingItemId ?? ''}`;
                return (
                  <label key={key} htmlFor={`bg-${key}`}
                    style={{ display: 'block', fontSize: 13, padding: '2px 0' }}>
                    <input id={`bg-${key}`} type="checkbox" checked={chosen.has(key)}
                      aria-label={`scope-${x.itemCode ?? x.typeCode}`}
                      onChange={() => toggle(key)} style={{ marginRight: 6 }} />
                    {x.itemName ?? x.typeName}
                  </label>
                );
              })}
            </fieldset>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Button type="button" variant="primary" data-testid="save-branding"
            disabled={busy || !cycleId || !chargeBasis} onClick={() => void saveBranding()}>
            {tr('vendorProfile.save')}
          </Button>
        </div>
      </section>

      <section aria-label="vendor-geography-section" style={s.card}>
        <h3 style={{ fontSize: 14, marginTop: 0 }}>{tr('vendorProfile.geography')}</h3>
        <p style={{ fontSize: 13, color: s.muted, marginTop: 0 }}>{tr('vendorProfile.geoHelp')}</p>

        {scopes.length === 0 ? (
          <p style={{ color: s.muted }} data-testid="geo-empty">{tr('vendorProfile.noGeography')}</p>
        ) : (
          <DataTable<GeoScopeRow>
            ariaLabel="vendor-geography"
            testId="vendor-geography"
            rows={[...scopes]}
            rowKey={(g) => g.scopeId}
            columns={[
              { key: 'level', header: tr('vendorProfile.geoLevel'), render: (g) => g.scopeType },
              {
                key: 'place', header: tr('vendorProfile.geoPlace'),
                render: (g) => g.tehsilName ?? g.districtName ?? g.stateName ?? '\u2014',
              },
              { key: 'from', header: tr('vendorProfile.validFrom'), render: (g) => day(g.effectiveFromDate) },
              {
                key: 'actions', header: tr('vendorProfile.colActions'),
                render: (g) => (
                  <Button type="button" variant="danger" data-testid={`remove-scope-${g.scopeId}`}
                    disabled={busy} onClick={() => void removePlace(g.scopeId)}>
                    {tr('vendorProfile.remove')}
                  </Button>
                ),
              },
            ]}
          />
        )}

        {/* Places are picked from lists, never typed. */}
        <div style={{ ...row, alignItems: 'flex-end', marginTop: 12 }}>
          <VendorGeographyPicker value={geo} onChange={setGeo} deepest="tehsilId" idPrefix="vp-scope" />
          <Button type="button" variant="secondary" data-testid="add-geography"
            disabled={busy} onClick={() => void addPlace()}>
            {tr('vendorProfile.addGeography')}
          </Button>
        </div>
      </section>
    </>
  );
}
